import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Box, Typography, Button, Stack,
    Avatar, Input, Select, Option, Alert, Chip, CircularProgress, IconButton,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon      from '@mui/icons-material/ArrowBackRounded';
import SecurityRoundedIcon       from '@mui/icons-material/SecurityRounded';
import VisibilityRoundedIcon     from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon  from '@mui/icons-material/VisibilityOffRounded';
import CameraAltRoundedIcon      from '@mui/icons-material/CameraAltRounded';
import DeleteOutlineRoundedIcon  from '@mui/icons-material/DeleteOutlineRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetEmployee,
    APIGetRoles,
    APIGetRoleDetail,
    APICreateEmployee,
    APIUpdateEmployee,
    APIUploadAvatar,
    APIDeleteAvatar,
    parseApiError,
} from '../../api';


// ─── Поле формы ───────────────────────────────────────────────────────────────
function FormField({ label, children, error }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: error ? '#f87171' : 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                {label}
            </Typography>
            {children}
            {error && (
                <Typography sx={{ fontSize: 11, color: '#f87171', mt: 0.4, fontWeight: 600 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}


// ─── Поле пароля с показом/скрытием ──────────────────────────────────────────
function PasswordInput({ value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={onChange}
            placeholder={placeholder || 'Минимум 6 символов'}
            endDecorator={
                <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setShow(v => !v)}
                    sx={{ color: 'var(--joy-palette-text-tertiary)' }}
                >
                    {show
                        ? <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                        : <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                    }
                </IconButton>
            }
            sx={{
                bgcolor: 'var(--joy-palette-background-level2)',
                border: '1px solid var(--joy-palette-divider)',
                color: 'white',
                fontSize: 13,
                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
            }}
        />
    );
}


// ─── Загрузка/смена/удаление аватарки (только при редактировании) ─────────────
function AvatarUploader({ employee, onUpdated }) {
    const { token, showToast } = useContext(AppContext);
    const [uploading, setUploading] = useState(false);
    const [localUrl, setLocalUrl] = useState(employee.avatar_url || null);
    const fileRef = useRef(null);

    const handlePick = () => fileRef.current?.click();

    const handleChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            showToast('Файл больше 5 МБ', 'danger');
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            showToast('Допустимы только JPG / PNG / WEBP', 'danger');
            return;
        }

        setUploading(true);
        const res = await APIUploadAvatar(token, employee.id, file);
        setUploading(false);

        if (res?.error) {
            const msg = typeof res.error === 'string' ? res.error : (res.error.error || 'Ошибка загрузки');
            showToast(msg, 'danger');
            return;
        }

        const url = res.avatar_url ? `${res.avatar_url}?t=${Date.now()}` : null;
        setLocalUrl(url);
        showToast('Аватарка обновлена ✓');
        if (onUpdated) onUpdated(url);

        e.target.value = '';
    };

    const handleDelete = async () => {
        setUploading(true);
        const res = await APIDeleteAvatar(token, employee.id);
        setUploading(false);

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить фото'), 'danger');
            return;
        }
        setLocalUrl(null);
        showToast('Фото удалено');
        if (onUpdated) onUpdated(null);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ position: 'relative' }}>
                <Avatar sx={{ width: 64, height: 64, fontSize: 20, fontWeight: 700 }} src={localUrl || undefined}>
                    {(employee.first_name?.[0] || '?').toUpperCase()}
                </Avatar>
                {uploading && (
                    <Box sx={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.5)',
                    }}>
                        <CircularProgress size="sm" />
                    </Box>
                )}
            </Box>

            <Stack spacing={0.6}>
                <Button
                    size="sm" variant="outlined" color="neutral"
                    onClick={handlePick} disabled={uploading}
                    startDecorator={<CameraAltRoundedIcon sx={{ fontSize: 14 }} />}
                >
                    {localUrl ? 'Сменить' : 'Загрузить'}
                </Button>
                {localUrl && (
                    <Button
                        size="sm" variant="plain" color="danger"
                        onClick={handleDelete} disabled={uploading}
                        startDecorator={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                    >
                        Удалить фото
                    </Button>
                )}
            </Stack>

            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleChange}
                style={{ display: 'none' }}
            />
        </Box>
    );
}


// Общие стили выпадающих списков (тёмная тема)
const selectSx = {
    color: 'var(--joy-palette-text-primary)',
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13,
    '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
};
const selectSlotProps = {
    listbox: {
        sx: {
            bgcolor: 'var(--joy-palette-background-level2)',
            border: '1px solid var(--joy-palette-divider)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            '& .MuiOption-root': {
                color: 'var(--joy-palette-text-primary)',
                fontSize: 13,
                '&:hover': { bgcolor: 'rgba(162,38,226,0.12)', color: 'var(--joy-palette-text-primary)' },
                '&[aria-selected="true"]': { bgcolor: 'rgba(162,38,226,0.2)', color: 'var(--joy-palette-primary-300)', fontWeight: 600 },
            },
        },
    },
};


// ─── Страница: создать / редактировать сотрудника ─────────────────────────────
export default function EmployeeFormPage() {
    const { token, showToast } = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();              // есть → редактирование, нет → создание
    const isEdit = Boolean(id);

    const [employee, setEmployee] = useState(null);
    const [roles,    setRoles]    = useState([]);
    const [loading,  setLoading]  = useState(isEdit);   // сотрудника грузим только в edit
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState('');

    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
        role: 'photographer', custom_role_id: null, password: '',
        is_blocked: false, is_excluded_from_payroll: false,
    });

    const [errs, setErrs] = useState({});

    // ── Загрузка ролей (с деталями для превью прав) + сотрудника (в режиме edit) ──
    useEffect(() => {
        let alive = true;
        (async () => {
            // Роли — с подгрузкой деталей (чтобы показать превью прав)
            const rolesData = await APIGetRoles(token);
            if (alive && Array.isArray(rolesData)) {
                const detailed = await Promise.all(
                    rolesData.map(async (r) => {
                        const d = await APIGetRoleDetail(token, r.id);
                        return d?.id ? d : r;
                    })
                );
                if (alive) setRoles(detailed);
            }

            // Сотрудник — только при редактировании
            if (isEdit) {
                const emp = await APIGetEmployee(token, id);
                if (!alive) return;
                if (emp?.id) {
                    setEmployee(emp);
                    setForm({
                        first_name: emp.first_name || '',
                        last_name:  emp.last_name  || '',
                        email:      emp.email      || '',
                        phone:      emp.phone      || '',
                        date_of_birth: emp.date_of_birth || '',
                        role:       emp.role       || 'photographer',
                        custom_role_id: emp.custom_role || null,
                        password:   '',
                        is_blocked: emp.is_blocked || false,
                        is_excluded_from_payroll: emp.is_excluded_from_payroll || false,
                    });
                    setLoading(false);
                } else {
                    showToast('Сотрудник не найден', 'danger');
                    navigate('/manager/employees');
                }
            }
        })();
        return () => { alive = false; };
    }, [token, id, isEdit, navigate, showToast]);

    const set = (field) => (e) => {
        const val = typeof e === 'string' ? e : e.target.value;
        setForm(prev => ({ ...prev, [field]: val }));
        setErrs(prev => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.first_name?.trim()) e.first_name = 'Укажите имя';
        if (!isEdit) {
            if (!form.email?.trim())                     e.email    = 'Укажите email';
            else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email    = 'Некорректный email';
            if (form.phone?.trim() && !/^[+\d\s()-]{6,20}$/.test(form.phone)) e.phone = 'Некорректный телефон';
            if (!form.password)                          e.password = 'Укажите пароль';
            else if (form.password.length < 6)           e.password = 'Минимум 6 символов';
        } else {
            if (form.phone && !/^[+\d\s()-]{6,20}$/.test(form.phone)) e.phone = 'Некорректный телефон';
        }
        return e;
    };

    const handleSave = async () => {
        const e = validate();
        setErrs(e);
        if (Object.keys(e).length) return;

        setSaving(true);
        setError('');

        let res;
        if (isEdit) {
            const payload = {
                first_name: form.first_name,
                last_name:  form.last_name,
                phone:      form.phone,
                date_of_birth: form.date_of_birth || null,
                role:       form.role,
                is_blocked: form.is_blocked,
                is_excluded_from_payroll: form.is_excluded_from_payroll,
                custom_role_id: form.custom_role_id || null,
            };
            res = await APIUpdateEmployee(token, id, payload);
        } else {
            res = await APICreateEmployee(token, { ...form, date_of_birth: form.date_of_birth || null });
        }

        setSaving(false);
        if (res?.error) {
            setError(parseApiError(res.error, 'Не удалось сохранить сотрудника'));
            return;
        }
        showToast(isEdit ? 'Сотрудник обновлён ✓' : 'Сотрудник создан ✓');
        navigate('/manager/employees');
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
                <Header />
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    const selectedRole = form.custom_role_id ? roles.find(r => r.id === form.custom_role_id) : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 560, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3, pb: 12 }}>
                {/* Шапка */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager/employees')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--joy-palette-text-primary)' }}>
                        {isEdit ? 'Редактировать сотрудника' : 'Новый сотрудник'}
                    </Typography>
                </Box>

                {/* Аватар — только при редактировании */}
                {isEdit && employee && (
                    <AvatarUploader
                        employee={employee}
                        onUpdated={(url) => setEmployee(prev => (prev ? { ...prev, avatar_url: url } : prev))}
                    />
                )}

                <Stack spacing={1.8}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <FormField label="Имя *" error={errs.first_name}>
                            <Input value={form.first_name} onChange={set('first_name')} placeholder="Иван" sx={{ ...(errs.first_name && { borderColor: '#f87171' }) }} />
                        </FormField>
                        <FormField label="Фамилия">
                            <Input value={form.last_name} onChange={set('last_name')} placeholder="Иванов" />
                        </FormField>
                    </Box>

                    {!isEdit && (
                        <FormField label="Email *" error={errs.email}>
                            <Input type="email" value={form.email} onChange={set('email')} placeholder="ivan@example.com" sx={{ ...(errs.email && { borderColor: '#f87171' }) }} />
                        </FormField>
                    )}

                    <FormField label={isEdit ? 'Телефон' : 'Телефон *'} error={errs.phone}>
                        <Input value={form.phone} onChange={set('phone')} placeholder="+7 999 000 00 00" sx={{ ...(errs.phone && { borderColor: '#f87171' }) }} />
                    </FormField>

                    <FormField label="Дата рождения">
                        <Input
                            type="date"
                            value={form.date_of_birth}
                            onChange={set('date_of_birth')}
                            slotProps={{ input: { max: new Date().toISOString().slice(0, 10) } }}
                        />
                    </FormField>

                    <FormField label="Роль *">
                        <Select
                            value={form.role}
                            onChange={(_, val) => val && setForm(prev => ({ ...prev, role: val }))}
                            sx={selectSx}
                            slotProps={selectSlotProps}
                        >
                            <Option value="photographer">Фотограф</Option>
                            <Option value="admin">Админ точки</Option>
                            <Option value="admin_photographer">Админ + Фотограф</Option>
                            <Option value="manager">Руководитель</Option>
                        </Select>
                    </FormField>

                    <FormField label="Набор прав">
                        <Select
                            value={form.custom_role_id || ''}
                            onChange={(_, val) => setForm(prev => ({ ...prev, custom_role_id: val || null }))}
                            placeholder="Выберите роль"
                            sx={selectSx}
                            slotProps={selectSlotProps}
                        >
                            <Option value="">Без роли (стандартный)</Option>
                            {roles.map(r => (
                                <Option key={r.id} value={r.id}>
                                    {r.name} ({r.permissions?.length ?? 0} прав)
                                </Option>
                            ))}
                        </Select>
                    </FormField>

                    {/* Превью прав выбранной роли */}
                    {selectedRole && (
                        <Box sx={{ p: 1.5, borderRadius: 'var(--joy-radius-lg)', border: '1px solid rgba(162,38,226,0.15)', bgcolor: 'rgba(162,38,226,0.04)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <SecurityRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-primary-400)' }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-primary-300)' }}>
                                    Права роли «{selectedRole.name}»
                                </Typography>
                            </Box>
                            {selectedRole.permissions?.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selectedRole.permissions.map(p => (
                                        <Chip key={p.id || p.tag} size="sm" sx={{ fontSize: 9, fontWeight: 600, bgcolor: 'rgba(162,38,226,0.08)', color: 'var(--joy-palette-text-secondary)', border: 'none' }}>
                                            {p.name || p.tag}
                                        </Chip>
                                    ))}
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                    {selectedRole.permissions_count ?? 0} прав (откройте роль для деталей)
                                </Typography>
                            )}
                        </Box>
                    )}

                    {!isEdit && (
                        <FormField label="Пароль *" error={errs.password}>
                            <PasswordInput value={form.password} onChange={set('password')} />
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Статус">
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[
                                    { val: false, label: 'Активен',      color: '#22c55e', bg: 'rgba(34,197,94,0.1)'  },
                                    { val: true,  label: 'Заблокирован', color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
                                ].map(opt => (
                                    <Box
                                        key={String(opt.val)}
                                        onClick={() => setForm(p => ({ ...p, is_blocked: opt.val }))}
                                        sx={{
                                            flex: 1, textAlign: 'center', py: 1, borderRadius: 'var(--joy-radius-md)',
                                            border: '1.5px solid',
                                            borderColor: form.is_blocked === opt.val ? opt.color : 'var(--joy-palette-divider)',
                                            bgcolor: form.is_blocked === opt.val ? opt.bg : 'transparent',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: form.is_blocked === opt.val ? opt.color : 'var(--joy-palette-text-secondary)' }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Учёт в системе">
                            <Box
                                onClick={() => setForm(p => ({ ...p, is_excluded_from_payroll: !p.is_excluded_from_payroll }))}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                                    borderRadius: 'var(--joy-radius-md)', border: '1px solid',
                                    borderColor: form.is_excluded_from_payroll ? 'rgba(245,158,11,0.5)' : 'var(--joy-palette-divider)',
                                    bgcolor: form.is_excluded_from_payroll ? 'rgba(245,158,11,0.08)' : 'var(--joy-palette-background-level2)',
                                    cursor: 'pointer', transition: 'all .15s',
                                }}
                            >
                                <Box sx={{
                                    width: 18, height: 18, borderRadius: 4, border: '2px solid',
                                    borderColor: form.is_excluded_from_payroll ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                                    bgcolor: form.is_excluded_from_payroll ? '#f59e0b' : 'transparent',
                                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {form.is_excluded_from_payroll && (
                                        <Typography sx={{ color: '#fff', fontSize: 12, lineHeight: 1, fontWeight: 900 }}>✓</Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        Не учитывать в графиках, штрафах, премиях
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                        Сотрудник остаётся доступным в управлении, но скрыт из рабочих списков
                                    </Typography>
                                </Box>
                            </Box>
                        </FormField>
                    )}

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Кнопки */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            loading={saving}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, border: 'none',
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                            }}
                        >
                            {isEdit ? 'Сохранить' : 'Создать'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/manager/employees')}
                            sx={{
                                borderColor: 'var(--joy-palette-divider)',
                                color: 'var(--joy-palette-text-secondary)',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </motion.div>
    );
}